"use client";

import cn from "clsx";
import { AnimatePresence, motion } from "motion/react";
import { useRef, useState } from "react";
import { useOnInView } from "react-intersection-observer";

type LazyImageProps = {
  src: string;
  alt?: string;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
};

export const LazyImage = ({ src, alt, placeholder, className, style }: LazyImageProps) => {
  const [showPlaceholder, setShowPlaceholder] = useState(!!placeholder);
  const imgRef = useRef<HTMLImageElement>(null);

  const loadImage = (src: string) => {
    const newImage = new Image(); // Create a native HTML Image object programmatically
    newImage.src = src;

    newImage.onload = () => {
      // When the programmatic image loads, update the ref's current src
      if (imgRef.current) {
        imgRef.current.src = src;
        setShowPlaceholder(false);
      }
    };

    newImage.onerror = () => {
      // Handle error state
      setShowPlaceholder(false);
      console.error("Failed to load image");
      // TODO: Set broken image link placeholder image
    };
  };

  const containerRef = useOnInView(
    (inView) => {
      if (inView) {
        loadImage(src);
      }
    },
    {
      threshold: 0,
      triggerOnce: true,
    },
  );

  return (
    <div
      className={cn("relative overflow-hidden flex flex-1 justify-center group", className)}
      style={style}
      ref={containerRef}
    >
      {/* Placeholder Image */}
      <AnimatePresence>
        {placeholder && showPlaceholder && (
          <motion.img
            key={placeholder}
            src={placeholder}
            alt={alt || "placeholder image"}
            className="object-cover w-full h-full absolute top-0 left-0 blur-lg scale-110"
            exit={{
              opacity: 0,
            }}
          />
        )}
      </AnimatePresence>
      <img ref={imgRef} key={src} alt={alt || "image"} className="object-cover w-full h-full" />
    </div>
  );
};
